import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Resource } from '@/lib/api/types'

export type HelpKind = 'guide' | 'troubleshooting' | 'faq'
export type HelpStatus = 'draft' | 'published'

export interface HelpCategory {
  id: string
  slug: string
  name: string
  description: string | null
  position: number
  articles: number
}

export interface HelpArticleSummary {
  id: string
  title: string
  kind: HelpKind
  slug: string
  status: HelpStatus
  category: { id: string; name: string } | null
  url: string
  updated_at: string | null
  updated_by: string | null
  /** Views in the last 30 days (counted since 2026-10-16; bots left out). */
  views_30d?: number
  /** Yes and No answers to "Was this article helpful?" in the last 30 days. */
  helpful_30d?: number
  not_helpful_30d?: number
}

export interface HelpArticle extends HelpArticleSummary {
  summary: string | null
  body: string
  position: number
  published_at: string | null
}

export interface HelpArticleList {
  data: HelpArticleSummary[]
  meta: {
    categories: HelpCategory[]
    kinds: Record<HelpKind, string>
    support_url: string
    can_manage_categories: boolean
    /** How the help center is used, counted since `counting_since`. */
    stats?: {
      published: number
      drafts: number
      views_this_month: number
      views_last_month: number
      /** The last 8 weeks, oldest first. */
      views_weekly: number[]
      misses_this_month: number
      misses_last_month: number
      top_misses: { query: string; searches: number }[]
      helpful_this_month: number
      not_helpful_this_month: number
      helpful_last_month: number
      not_helpful_last_month: number
      /** Answered No most often in the last 30 days. */
      least_helpful: { id: string; title: string; helpful: number; not_helpful: number }[]
      counting_since: string
    }
  }
}

export interface HelpFilters {
  kind?: HelpKind | ''
  status?: HelpStatus | ''
  q?: string
}

export interface HelpArticleInput {
  title: string
  kind: HelpKind
  help_category_id: string | null
  summary: string | null
  body: string
  slug?: string | null
  status: HelpStatus
}

const keys = {
  list: (f: HelpFilters) => ['help', 'articles', f] as const,
  one: (id: string) => ['help', 'article', id] as const,
}

export function useHelpArticles(filters: HelpFilters) {
  const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) as Record<string, string>
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: () => http.get<HelpArticleList>('/help/articles', query),
    placeholderData: keepPreviousData,
  })
}

export function useHelpArticle(id: string | undefined) {
  return useQuery({
    queryKey: keys.one(id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => (await http.get<Resource<HelpArticle>>(`/help/articles/${id ?? ''}`)).data,
  })
}

/** Create (no id) or update an article. */
export function useSaveHelpArticle(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<HelpArticleInput>) =>
      id === undefined
        ? (await http.post<Resource<HelpArticle>>('/help/articles', input)).data
        : (await http.patch<Resource<HelpArticle>>(`/help/articles/${id}`, input)).data,
    onSuccess: (article) => {
      queryClient.setQueryData(keys.one(article.id), article)
      void queryClient.invalidateQueries({ queryKey: ['help', 'articles'] })
    },
  })
}

export function useDeleteHelpArticle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/help/articles/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['help'] }),
  })
}

/** How the body looks on the help center (rendered by the API, so it's exact). */
export function useHelpPreview(body: string, enabled: boolean) {
  return useQuery({
    queryKey: ['help', 'preview', body],
    enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: async () => (await http.post<Resource<{ html: string }>>('/help/preview', { body })).data.html,
  })
}

export function useHelpCategoryAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      input:
        | { action: 'create'; name: string; description: string | null }
        | { action: 'update'; id: string; name: string; description: string | null; position: number }
        | { action: 'delete'; id: string },
    ) => {
      switch (input.action) {
        case 'create':
          return http.post('/help/categories', { name: input.name, description: input.description })
        case 'update':
          return http.patch(`/help/categories/${input.id}`, {
            name: input.name,
            description: input.description,
            position: input.position,
          })
        case 'delete':
          return http.delete(`/help/categories/${input.id}`)
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['help'] }),
  })
}
